import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import {
    IAudioProviderInfoDTO,
    IRecurringZoomMeetingDTO,
    IRequestTelephoneNumberSet,
    IScheduledMeetingsListDTO,
    IScheduledMeetingsListRequest,
    IScheduledZoomMeetingDetailsDTO,
    IScheduledZoomMeetingsStatusDTO,
    IScheduledZoomWebinarDetailsDTO,
    ITelephoneNumberSetRequest,
    ITelephoneNumberSetsListDTO,
    ITrackedZoomUserDTO,
    ITrackedZoomUsersListDTO,
    IUpdateNumberSetDTO,
    IUpdateZoomUsersRequest,
    IZoomDAO,
    IZoomGroupDTO,
    IZoomMeeting,
    IZoomMeetingDTO,
    IZoomMeetingListItemDTO,
    IZoomTelephoneNumberSetDTO,
    IZoomUsersRequest,
    IZoomUsersResponse,
    IZoomWebinarReportDTO,
    NumberSetRequestDataWithFile,
    NumberSetRequestProviderData,
    PanelistLinkDeletionRequestParams,
    UniqueDialInData,
    UniqueDialInRequestParams,
} from '../zoom.dao';
import { DeepReadonly, RetypePartial } from '../../infrastructure/type-extensions';
import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { IEventRequestParams, replaceSortRequestParamsWithSortString } from '../shared.dto';
import {
    CREATE_TELEPHONE_NUMBER_SETS,
    GET_TRACKED_ZOOM_USER,
    GET_ZOOM_GROUPS,
    GET_ZOOM_USERS,
    GET_ZOOM_WEBINAR_REPORTS,
    MEETINGS,
    MEETINGS_STATUS,
    REFRESH_ALL_ZOOM_MEETINGS,
    REFRESH_HOST_KEYS,
    SINGLE_ZOOM_MEETING_DETAILS,
    TELEPHONE_NUMBER_SETS,
    TRACKED_ZOOM_USERS,
    UPDATE_TELEPHONE_NUMBER_SETS,
    UPDATE_TRACKED_USERS,
    UPDATE_ZOOM_CONNECTION_URL,
    UPDATE_ZOOM_USER_HOST_KEY,
    WEBINARS,
    ZOOM_MEETING_DETAILS,
    ZOOM_MEETING_WEBINAR_DETAILS,
    ZOOM_MEETINGS,
    ZOOM_WEBINAR_ATTENDEE_REPORT,
    ZOOM_WEBINAR_DETAILS,
    ZOOM_WEBINAR_REGISTRANTS_REPORT,
    ZOOM_WEBINARS,
    ZOOM_WEBINARS_EDIT_URL,
    DEACTIVATE_TELEPHONE_NUMBER_SETS,
    FETCH_TELEPHONE_NUMBER_SET,
    UNIQUE_DAILIN_CONNECTION,
    DOWNLOAD_TELEPHONE_NUMBER_SET,
    AUDIO_BRIDGE_URL,
    DELETE_PANELIST_LINK,
    ZOOM_MEETING_REGISTRANTS_REPORT,
    PHONE_NUMBER_SET,
} from '../../constants/api/zoom-api.constants';
import { JobDtoType } from '../../domain/jobs';
import { IConnectionDetailsDTO, IMeetingConnectionRequestParams } from '../connections.dao';
import { RADIX_PARAM } from '../../constants/common.constants';

type IZoomMeetingListDTOInternal = RetypePartial<IZoomMeetingListItemDTO, 'startTime' | 'endTime', string>;
type IZoomMeetingDetailsDTOInternal = RetypePartial<IScheduledZoomMeetingDetailsDTO, 'startTime' | 'endTime', string>;
type IZoomWebinarDetailsDTOInternal = RetypePartial<IScheduledZoomWebinarDetailsDTO, 'startTime' | 'endTime', string>;
type IScheduledMeetingsListDTOInternal = RetypePartial<IScheduledMeetingsListDTO, 'content', IZoomMeetingListDTOInternal[]>;

class ZoomDAO extends IZoomDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter,
    ) {
        super();
    }

    public async getTrackedUsers(params: IZoomUsersRequest): Promise<ITrackedZoomUsersListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        return (await this.apiClient.get<ITrackedZoomUsersListDTO>(TRACKED_ZOOM_USERS,
            { ...paramsWithSortString, ...params })).parsedResponse;
    }

    public async getZoomUsers(pageSize: number, pageNumber: number): Promise<IZoomUsersResponse> {
        return (await this.apiClient.get<IZoomUsersResponse>(GET_ZOOM_USERS, { pageSize: pageSize, pageNumber: pageNumber })).parsedResponse;
    }

    public async getScheduledMeetings(params: IScheduledMeetingsListRequest): Promise<IScheduledMeetingsListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);

        const response = (await this.apiClient.get<IScheduledMeetingsListDTOInternal>(ZOOM_MEETINGS, {
            ...paramsWithSortString,
            startDateTime: params.startDateTime ? this.apiDateConverter.dateToAPIString(params.startDateTime) : '',
            endDateTime: params.endDateTime ? this.apiDateConverter.dateToAPIString(params.endDateTime) : ''
        })).parsedResponse;

        return {
            ...response,
            content: response.content.map(x => this.mapInternalZoomMeetingDTOToPublic(x)),
        };
    }

    public async updateTrackedUsers(updateUsersRequest: Partial<DeepReadonly<IUpdateZoomUsersRequest>>): Promise<void> {
        await this.apiClient.post(UPDATE_TRACKED_USERS, {
            addItems: updateUsersRequest.addItems || [],
            deleteItems: updateUsersRequest.deleteItems || [],
        });
    }

    public async refreshAllZoomMeetings() {
        return (await this.apiClient.post<JobDtoType>(REFRESH_ALL_ZOOM_MEETINGS)).parsedResponse;
    }

    public async refreshHostKeys() {
        return (await this.apiClient.post<JobDtoType>(REFRESH_HOST_KEYS)).parsedResponse;
    }

    public async getScheduledMeetingDetails(meetingId: number): Promise<IScheduledZoomMeetingDetailsDTO> {
        const meetingDetails = await this.apiClient.get<IZoomMeetingDetailsDTOInternal>(`${ZOOM_MEETINGS}/${meetingId}`);
        return this.mapInternalZoomMeetingDTOToPublic(meetingDetails.parsedResponse);
    }

    public async getUpdatedMeetingDetails(params: IMeetingConnectionRequestParams & IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> {
        const meetingDetails = await this.apiClient.get<IConnectionDetailsDTO>(
            UPDATE_ZOOM_CONNECTION_URL, params);
        return this.mapInternalZoomMeetingDTOToPublic(meetingDetails.parsedResponse);
    }

    public async getScheduledWebinarDetails(webinarId: number): Promise<IScheduledZoomWebinarDetailsDTO> {
        const meetingDetails = await this.apiClient.get<IZoomWebinarDetailsDTOInternal>(`${ZOOM_MEETING_WEBINAR_DETAILS}/${webinarId}`);
        return this.mapInternalZoomMeetingDTOToPublic(meetingDetails.parsedResponse);
    }

    public async getZoomMeetingDetails(meetingId: number): Promise<IZoomMeetingListItemDTO> {
        const meetingDetails = await this.apiClient.get<IZoomWebinarDetailsDTOInternal>(`${ZOOM_MEETINGS}/${meetingId}`);
        return this.mapInternalZoomMeetingDTOToPublic(meetingDetails.parsedResponse);
    }

    public async getZoomConnectionDetails(zoomId: string): Promise<IZoomMeetingListItemDTO> {
        const meetingDetails = await this.apiClient.get<IZoomWebinarDetailsDTOInternal>(`${ZOOM_MEETING_DETAILS}/${zoomId}`);
        return this.mapInternalZoomMeetingDTOToPublic(meetingDetails.parsedResponse);
    }

    public async scheduleNewMeeting(params: IZoomMeetingDTO): Promise<IZoomMeeting> {
        return (
            await this.apiClient.post<IZoomMeeting>(ZOOM_MEETINGS, {
                ...params,
                startTime: this.apiDateConverter.dateToAPIString(params.startTime, params.timeZone),
                endTime: this.apiDateConverter.dateToAPIString(params.endTime, params.timeZone),
            })
        ).parsedResponse;
    }

    public async scheduleNewRecurringMeeting(params: IRecurringZoomMeetingDTO): Promise<IZoomMeeting> {
        return (
            await this.apiClient.post<IZoomMeeting>(ZOOM_MEETINGS, {
                ...params,
            })
        ).parsedResponse;
    }

    public async scheduleNewWebinar(params: IEventRequestParams & IZoomMeetingDTO): Promise<IZoomMeeting> {
        return (
            await this.apiClient.post<IZoomMeeting>(ZOOM_WEBINARS, {
                ...params,
                startTime: this.apiDateConverter.dateToAPIString(params.startTime, params.timeZone),
                endTime: this.apiDateConverter.dateToAPIString(params.endTime, params.timeZone),
            })
        ).parsedResponse;
    }

    public async updateWebinar(
        params: IEventRequestParams & { webinarId: string } & IZoomMeetingDTO
    ): Promise<IZoomMeeting> {
        return (
            await this.apiClient.put<IZoomMeeting>(ZOOM_WEBINARS_EDIT_URL, {
                ...params,
                startTime: this.apiDateConverter.dateToAPIString(params.startTime, params.timeZone),
                endTime: this.apiDateConverter.dateToAPIString(params.endTime, params.timeZone),
            })
        ).parsedResponse;
    }

    public async updateMeeting(meetingId: number, params: IZoomMeetingDTO): Promise<IZoomMeeting> {
        return (await this.apiClient.put<IZoomMeeting>(`${ZOOM_MEETINGS}/${meetingId}`, {
            ...params,
            startTime: this.apiDateConverter.dateToAPIString(params.startTime, params.timeZone),
            endTime: this.apiDateConverter.dateToAPIString(params.endTime, params.timeZone)
        })).parsedResponse;
    }

    private mapInternalZoomMeetingDTOToPublic(zoomMeeting) {
        return {
            ...zoomMeeting,
            startTime: zoomMeeting.startTime ? this.apiDateConverter.apiStringToDate(zoomMeeting.startTime) : null,
            endTime: zoomMeeting.endTime ? this.apiDateConverter.apiStringToDate(zoomMeeting.endTime) : null,
        };
    }

    public async deleteScheduledMeeting(meetingId: string): Promise<void> {
        return (await this.apiClient.delete<void>(`${ZOOM_MEETINGS}/${meetingId}`)).parsedResponse;
    }

    public async deleteWebinardMeeting(params: IEventRequestParams & { webinarId: string }): Promise<void> {
        return (await this.apiClient.delete<void>(ZOOM_WEBINARS_EDIT_URL, params)).parsedResponse;
    }

    public async refreshUserMeetingsSchedule(userZoomId: string): Promise<void> {
        this.apiClient.get<void>(`${TRACKED_ZOOM_USERS}/${userZoomId}/${MEETINGS}`);
    }

    public async refreshWebinars(userZoomId: string): Promise<void> {
        this.apiClient.get<void>(`${TRACKED_ZOOM_USERS}/${userZoomId}/${WEBINARS}`);
    }

    public async getUserScheduledMeetingsStatus(userZoomId: string): Promise<IScheduledZoomMeetingsStatusDTO> {
        const meetingsStatus = await this.apiClient.get<IScheduledZoomMeetingsStatusDTO>(`${TRACKED_ZOOM_USERS}/${userZoomId}/${MEETINGS_STATUS}`);
        return meetingsStatus.parsedResponse;
    }

    public async getGroups(): Promise<IZoomGroupDTO[]> {
        const response = await this.apiClient.get<IZoomGroupDTO[]>(GET_ZOOM_GROUPS);
        return response.parsedResponse;
    }

    public async getWebinarAttendeeReport(params: IEventRequestParams & { webinarId: string }) {
        await this.apiClient.getFile(ZOOM_WEBINAR_ATTENDEE_REPORT, params);
    }

    public async getWebinarRegistrationReport(params: IEventRequestParams & { webinarId: string }) {
        await this.apiClient.getFile(ZOOM_WEBINAR_REGISTRANTS_REPORT, params);
    }

    public async getZoomWebinarReports(params: IEventRequestParams) {
        const response = await this.apiClient.get<IZoomWebinarReportDTO[]>(GET_ZOOM_WEBINAR_REPORTS, params);
        return response.parsedResponse;
    }

    public async getZoomWebinarDetails(params: IEventRequestParams & { webinarId: string }) {
        const response = await this.apiClient.get<IScheduledZoomWebinarDetailsDTO>(ZOOM_WEBINAR_DETAILS, params);
        return response.parsedResponse;
    }

    public async updateZoomUserHostKey( params: { userZoomId: string }) {
        const response = await this.apiClient.put<JobDtoType>(UPDATE_ZOOM_USER_HOST_KEY, params);
        return response.parsedResponse;
    }

    public async getTrackedZoomUser(params: { userZoomId: string }) {
        const response = await this.apiClient.get<ITrackedZoomUserDTO>(GET_TRACKED_ZOOM_USER, params);
        return response.parsedResponse;
    }

    public async getSingleZoomMeetingDetails(params: { meetingId: string }) {
        const response = await this.apiClient.get<IScheduledZoomMeetingDetailsDTO>(SINGLE_ZOOM_MEETING_DETAILS, params);
        return response.parsedResponse;
    }

    public async getTelephoneNumberSets(params: ITelephoneNumberSetRequest): Promise<ITelephoneNumberSetsListDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);
        const response = (await this.apiClient.get<ITelephoneNumberSetsListDTO>(TELEPHONE_NUMBER_SETS,
            { ...paramsWithSortString, ...params }));
        return response.parsedResponse;
    }

    public async createTelephoneNumberSet(numberSetDetails: IRequestTelephoneNumberSet): Promise<ITelephoneNumberSetsListDTO> {
        const response = await this.apiClient.post<ITelephoneNumberSetsListDTO>(CREATE_TELEPHONE_NUMBER_SETS, {
            file : numberSetDetails.telephoneArchive,
            audioProvider: new Blob([JSON.stringify({
                name: numberSetDetails.name,
                provider: numberSetDetails.provider,
                oneTapFormula: numberSetDetails.oneTapFormula
            })], { type: 'application/json' })
        }, {
            ContentType: RequestContentType.formData,
        });
        return response.parsedResponse;
    }

    public async updateTelephoneNumberSet(numberSetDetails: IUpdateNumberSetDTO): Promise<ITelephoneNumberSetsListDTO> {
        let requestData: NumberSetRequestProviderData | NumberSetRequestDataWithFile = {
            audioProvider: new Blob([JSON.stringify({
                name: numberSetDetails.name,
                provider: numberSetDetails.provider,
                oneTapFormula: numberSetDetails.oneTapFormula
            })], { type: 'application/json' }),
            id: parseInt(numberSetDetails.id, RADIX_PARAM)
        };

        if (numberSetDetails.telephoneArchive) {
            requestData = {
                ...requestData,
                file: numberSetDetails.telephoneArchive
            } as NumberSetRequestDataWithFile;
        }

        const response = await this.apiClient.put<ITelephoneNumberSetsListDTO>(
            UPDATE_TELEPHONE_NUMBER_SETS,
            requestData,
            {
                ContentType: RequestContentType.formData,
            }
        );
        return response.parsedResponse;
    }

    public async deactivateTelephoneNumberSet(params: {audioProviderId: string}): Promise<void> {
        await this.apiClient.put<void>(DEACTIVATE_TELEPHONE_NUMBER_SETS, params);
    }

    public async fetchTelephoneNumberSet(params: {audioProviderId: string}): Promise<IZoomTelephoneNumberSetDTO> {
       const response = await this.apiClient.get<IZoomTelephoneNumberSetDTO>(FETCH_TELEPHONE_NUMBER_SET, params);
       return response.parsedResponse;
    }

    public async createUniqueDialInConnection(params: UniqueDialInRequestParams): Promise<UniqueDialInData> {
        const response = await this.apiClient.post<UniqueDialInData>(UNIQUE_DAILIN_CONNECTION, params);
        return response.parsedResponse;
    }

    public async downloadTelephoneNumberSet(params: {audioProviderId: string}): Promise<void> {
        await this.apiClient.getFile(DOWNLOAD_TELEPHONE_NUMBER_SET, params);
    }

    public async deleteScheduledAudioBridge(params: {audioBridgeId: string}): Promise<void> {
        const response = await this.apiClient.delete<void>(AUDIO_BRIDGE_URL, params);
        return response.parsedResponse;
    }

    public async deleteScheduledPanelistLink(params: PanelistLinkDeletionRequestParams): Promise<void> {
        const response = await this.apiClient.delete<void>(DELETE_PANELIST_LINK, params);
        return response.parsedResponse;
    }

    public async getZoomMeetingRegistrationReport(params: IEventRequestParams & { meetingId: string }): Promise<void> {
        await this.apiClient.get(ZOOM_MEETING_REGISTRANTS_REPORT, params);
    }

    public getPhoneNumbersSet = async (params: {audioProviderId: string}): Promise<IAudioProviderInfoDTO> => {
        const response = await this.apiClient.get<IAudioProviderInfoDTO>(PHONE_NUMBER_SET, params);
        return response.parsedResponse;
    };
}
export { ZoomDAO };
